import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  DollarSign,
  TrendingUp,
  FileText,
  BarChart3,
  Activity,
  CheckCircle2,
  Briefcase,
  Calendar,
  RefreshCw,
  Award,
  AlertTriangle,
} from "lucide-react";
import AdminLayout from "../components/layout/AdminLayout";
import {
  useAdminStore,
  type AccountPerformanceCard,
} from "../store/useAdminStore";

type Period = "all" | "7d" | "30d" | "90d";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const scoreTone = (score: number) => {
  if (score >= 75)
    return {
      ring: "stroke-emerald-500",
      text: "text-emerald-700",
      bg: "from-emerald-500 to-emerald-600",
      label: "Excellent",
    };
  if (score >= 50)
    return {
      ring: "stroke-blue-500",
      text: "text-blue-700",
      bg: "from-blue-500 to-blue-600",
      label: "Strong",
    };
  if (score >= 25)
    return {
      ring: "stroke-amber-500",
      text: "text-amber-700",
      bg: "from-amber-500 to-amber-600",
      label: "Needs Work",
    };
  return {
    ring: "stroke-rose-500",
    text: "text-rose-700",
    bg: "from-rose-500 to-rose-600",
    label: "Critical",
  };
};

const initials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const ScoreRing = ({ score }: { score: number }) => {
  const size = 140;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const offset = c - (c * pct) / 100;
  const tone = scoreTone(pct);
  return (
    <div className="relative flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="transparent"
            stroke="#f1f5f9"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="transparent"
            className={tone.ring}
            strokeWidth={stroke}
            strokeDasharray={c}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-[34px] font-black leading-none ${tone.text}`}>
            {pct}
          </span>
          <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-slate-500 mt-1">
            / 100
          </span>
        </div>
      </div>
      <span
        className={`mt-2 text-[10px] uppercase tracking-[0.15em] font-extrabold ${tone.text}`}
      >
        {tone.label}
      </span>
    </div>
  );
};

const MetricRow = ({
  icon: Icon,
  iconBg,
  label,
  value,
  sub,
}: {
  icon: any;
  iconBg: string;
  label: string;
  value: string;
  sub?: string;
}) => (
  <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
    <div className={`w-9 h-9 rounded-lg grid place-items-center ${iconBg}`}>
      <Icon size={16} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-500">
        {label}
      </p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
    <span className="text-[15px] font-extrabold text-slate-900 shrink-0">
      {value}
    </span>
  </div>
);

const AccountCard = ({ card }: { card: AccountPerformanceCard }) => {
  const m = card.metrics;
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">

      <div className="p-5 border-b border-slate-100 bg-gradient-to-br from-blue-50/40 to-white">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[14px] font-black shadow-sm">
            {initials(card.accountName)}
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
              Upwork Account
            </p>
            <h3 className="text-[18px] font-extrabold text-slate-900 leading-tight">
              {card.accountName}
            </h3>
          </div>
        </div>
      </div>

      <div className="p-5 border-b border-slate-100 flex flex-col items-center bg-gradient-to-b from-slate-50/40 to-white">
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-3 flex items-center gap-1.5">
          <Zap size={12} className="text-amber-500 fill-amber-500" />
          Bid Efficiency Score
        </p>
        <ScoreRing score={m.bidEfficiencyScore} />
        <div className="mt-4 grid grid-cols-4 gap-2 w-full">
          <ScoreInputChip
            label="Win"
            value={card.breakdown.scoreInputs.winRateScore}
          />
          <ScoreInputChip
            label="Conn"
            value={card.breakdown.scoreInputs.connectEffScore}
          />
          <ScoreInputChip
            label="Deal"
            value={card.breakdown.scoreInputs.dealSizeScore}
          />
          <ScoreInputChip
            label="Cat"
            value={card.breakdown.scoreInputs.categoryStrengthScore}
          />
        </div>
      </div>

      <div className="p-5">
        <MetricRow
          icon={Zap}
          iconBg="bg-amber-100 text-amber-700"
          label="Avg. Connects per Bid"
          value={String(m.avgConnectsPerBid || "—")}
          sub={`${m.totalConnects.toLocaleString()} total / ${m.totalBids} bids`}
        />
        <MetricRow
          icon={DollarSign}
          iconBg="bg-emerald-100 text-emerald-700"
          label="Avg. Project Amount"
          value={m.avgProjectAmount ? fmtMoney(m.avgProjectAmount) : "—"}
          sub={`${m.completedProjects} completed · ${fmtMoney(m.totalRevenue)} total`}
        />
        <MetricRow
          icon={TrendingUp}
          iconBg="bg-blue-100 text-blue-700"
          label="Top Converting Category"
          value={m.topConvertingCategory || "—"}
          sub={
            m.topConvertingCategory
              ? `${m.topCategoryConversionPct}% conversion in this category`
              : "Not enough data yet"
          }
        />
        <MetricRow
          icon={FileText}
          iconBg="bg-violet-100 text-violet-700"
          label="Avg. Proposals Created"
          value={`${m.avgProposalsPerMonth} / Month`}
          sub="Bids submitted per month (account-level activity)"
        />
        <MetricRow
          icon={Award}
          iconBg="bg-rose-100 text-rose-700"
          label="Win Rate"
          value={`${m.winRate}%`}
          sub={`${m.wonBids} won out of ${m.totalBids} bids`}
        />
      </div>

      <div className="px-5 py-3 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <Activity size={11} />
          Real-time
        </span>
        <button
          onClick={() => navigate(`/admin/projects?profile=${card.accountId}`)}
          className="text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1"
        >
          <BarChart3 size={11} />
          View projects
        </button>
      </div>
    </div>
  );
};

const ScoreInputChip = ({
  label,
  value,
}: {
  label: string;
  value: number;
}) => {
  const v = Math.round(value);
  const tone =
    v >= 75
      ? "bg-emerald-50 text-emerald-700"
      : v >= 50
      ? "bg-blue-50 text-blue-700"
      : v >= 25
      ? "bg-amber-50 text-amber-700"
      : "bg-rose-50 text-rose-700";
  return (
    <div className={`rounded-md p-1.5 text-center ${tone}`}>
      <p className="text-[8px] uppercase tracking-wider font-bold opacity-70">
        {label}
      </p>
      <p className="text-[11px] font-extrabold">{v}</p>
    </div>
  );
};

const AdminAccountPerformancePage = () => {
  const { accountPerformance, fetchAccountPerformance } = useAdminStore();
  const [period, setPeriod] = useState<Period>("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchAccountPerformance({ period }).finally(() => setLoading(false));
  }, [period, fetchAccountPerformance]);

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
              Account Performance
            </h1>
            <p className="text-[13px] text-slate-500 mt-1">
              Per-Upwork-account KPI snapshot · bidding efficiency, project
              value, category conversion, activity & composite score
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
              {(
                [
                  { key: "all", label: "All time" },
                  { key: "90d", label: "90 days" },
                  { key: "30d", label: "30 days" },
                  { key: "7d", label: "7 days" },
                ] as { key: Period; label: string }[]
              ).map((opt) => {
                const active = opt.key === period;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setPeriod(opt.key)}
                    className={`px-3 py-1.5 text-[12px] font-semibold rounded-md transition ${
                      active
                        ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <Calendar size={11} className="inline mr-1" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => {
                setLoading(true);
                fetchAccountPerformance({ period }).finally(() =>
                  setLoading(false)
                );
              }}
              className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3 flex items-start gap-3">
          <AlertTriangle
            size={16}
            className="text-amber-500 mt-0.5 shrink-0"
          />
          <p className="text-[12px] text-slate-600 leading-relaxed">
            <strong className="text-slate-900">Bid Efficiency Score</strong>{" "}
            (0–100) is a weighted composite of Win Rate (40%), Connect
            Efficiency (20%), Avg Deal Size (20%), and Top Category Conversion
            (20%).{" "}
            <span className="text-slate-500">
              Use the chips below the ring to see each input contribution.
            </span>
          </p>
        </div>

        {!accountPerformance ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 text-[13px]">
            Loading account performance...
          </div>
        ) : accountPerformance.accounts.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 text-[13px]">
            No Upwork accounts found.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {accountPerformance.accounts.map((card) => (
              <AccountCard key={card.accountId} card={card} />
            ))}
          </div>
        )}

        {accountPerformance && accountPerformance.accounts.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h3 className="text-[15px] font-bold text-slate-900 flex items-center gap-2">
                <BarChart3 size={16} className="text-[#0058c3]" />
                Account Comparison
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Side-by-side KPI table for the selected period
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200">
                    <Th>Account</Th>
                    <Th align="right">Avg Connects/Bid</Th>
                    <Th align="right">Avg Project $</Th>
                    <Th>Top Category</Th>
                    <Th align="right">Proposals/Mo</Th>
                    <Th align="right">Win Rate</Th>
                    <Th align="center">Efficiency Score</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {accountPerformance.accounts.map((card) => {
                    const m = card.metrics;
                    const tone = scoreTone(m.bidEfficiencyScore);
                    return (
                      <tr
                        key={card.accountId}
                        className="hover:bg-slate-50/50 transition"
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 grid place-items-center text-white text-[10px] font-bold">
                              {initials(card.accountName)}
                            </div>
                            <span className="text-[13px] font-bold text-slate-900">
                              {card.accountName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right text-[13px] text-slate-800 font-semibold">
                          {m.avgConnectsPerBid || "—"}
                        </td>
                        <td className="px-4 py-4 text-right text-[13px] font-bold text-slate-900 font-mono">
                          {m.avgProjectAmount
                            ? fmtMoney(m.avgProjectAmount)
                            : "—"}
                        </td>
                        <td className="px-4 py-4 text-[12px] text-slate-700">
                          {m.topConvertingCategory ? (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold">
                              {m.topConvertingCategory}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right text-[13px] text-slate-800 font-semibold">
                          {m.avgProposalsPerMonth}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span
                            className={`text-[12px] px-2 py-0.5 rounded-full font-bold ${
                              m.winRate >= 30
                                ? "bg-emerald-100 text-emerald-700"
                                : m.winRate >= 15
                                ? "bg-amber-100 text-amber-700"
                                : "bg-rose-100 text-rose-700"
                            }`}
                          >
                            {m.winRate}%
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span
                              className={`text-[16px] font-extrabold ${tone.text}`}
                            >
                              {m.bidEfficiencyScore}
                            </span>
                            <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full bg-gradient-to-r ${tone.bg}`}
                                style={{
                                  width: `${m.bidEfficiencyScore}%`,
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {accountPerformance && accountPerformance.accounts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CalloutCard
              icon={CheckCircle2}
              tone="emerald"
              title="Best Performer"
              body={(() => {
                const best = [...accountPerformance.accounts].sort(
                  (a, b) =>
                    b.metrics.bidEfficiencyScore -
                    a.metrics.bidEfficiencyScore
                )[0];
                return best
                  ? `${best.accountName} leads with ${best.metrics.bidEfficiencyScore}/100 efficiency`
                  : "No data yet";
              })()}
            />
            <CalloutCard
              icon={Briefcase}
              tone="blue"
              title="Biggest Deals"
              body={(() => {
                const top = [...accountPerformance.accounts].sort(
                  (a, b) =>
                    b.metrics.avgProjectAmount - a.metrics.avgProjectAmount
                )[0];
                return top && top.metrics.avgProjectAmount
                  ? `${top.accountName} avg ${fmtMoney(
                      top.metrics.avgProjectAmount
                    )}/project`
                  : "No completed projects yet";
              })()}
            />
            <CalloutCard
              icon={AlertTriangle}
              tone="amber"
              title="Needs Attention"
              body={(() => {
                const worst = [...accountPerformance.accounts].sort(
                  (a, b) =>
                    a.metrics.bidEfficiencyScore -
                    b.metrics.bidEfficiencyScore
                )[0];
                return worst
                  ? `${worst.accountName} score ${worst.metrics.bidEfficiencyScore}/100 — review strategy`
                  : "—";
              })()}
            />
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const Th = ({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}) => (
  <th
    className={`px-4 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-${align}`}
  >
    {children}
  </th>
);

const CalloutCard = ({
  icon: Icon,
  tone,
  title,
  body,
}: {
  icon: any;
  tone: "emerald" | "blue" | "amber";
  title: string;
  body: string;
}) => {
  const tones = {
    emerald: "from-emerald-50 to-emerald-100/40 text-emerald-700 ring-emerald-200",
    blue: "from-blue-50 to-blue-100/40 text-blue-700 ring-blue-200",
    amber: "from-amber-50 to-amber-100/40 text-amber-700 ring-amber-200",
  };
  return (
    <div
      className={`rounded-xl p-4 bg-gradient-to-br ring-1 ${tones[tone]}`}
    >
      <div className="flex items-start gap-3">
        <Icon size={20} />
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-80">
            {title}
          </p>
          <p className="text-[13px] font-semibold text-slate-800 mt-1 leading-snug">
            {body}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAccountPerformancePage;
